import { Component } from '@theme/component';
import { createEmblaCarousel, loadEmblaCarousel, shouldJump } from '@theme/embla';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { scrollIntoView } from '@theme/scrolling';
import { prefersReducedMotion } from '@theme/utilities';

// Thumbnail classes marking the selected slide.
const SELECTED_THUMBNAIL_CLASSES = ['ring-1', 'ring-primary', 'ring-offset-2'];

/**
 * A custom element that renders a media gallery.
 *
 * In the carousel presentation the slides are driven by Embla Carousel, set up through the
 * shared helpers in embla.js.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 * @property {HTMLElement} [viewport] - The carousel viewport.
 * @property {HTMLElement[]} [slides] - The carousel slides.
 * @property {HTMLElement[]} [thumbnails] - The thumbnail buttons.
 * @property {HTMLButtonElement} [previous] - The previous slide button.
 * @property {HTMLButtonElement} [next] - The next slide button.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#updateSelection();
    this.#setupCarousel();
  }

  #controller = new AbortController();

  /**
   * The Embla Carousel instance.
   * @type {any}
   */
  #embla;

  /**
   * The selected slide index, kept so a selection made before Embla loads is applied on init.
   */
  #selectedIndex = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#embla?.destroy();
    this.#embla = undefined;
  }

  /**
   * Selects a slide.
   * @param {number|string} index - The index of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  select(index, event, { animate = true } = {}) {
    const slideCount = this.refs.slides?.length ?? 0;
    const requestedIndex = typeof index === 'string' ? parseInt(index, 10) : index;

    if (!slideCount || Number.isNaN(requestedIndex)) return;

    event?.preventDefault();

    const clampedIndex = Math.min(Math.max(requestedIndex, 0), slideCount - 1);

    if (this.#embla) {
      this.#embla.scrollTo(clampedIndex, this.#jump(animate));
    } else {
      this.#selectedIndex = clampedIndex;
      this.#updateSelection();
    }
  }

  /**
   * Goes to the previous slide.
   * @param {Event} [event] - The event that triggered the navigation.
   */
  previous(event) {
    event?.preventDefault();
    if (this.#embla) this.#embla.scrollPrev(this.#jump());
    else this.select(this.#selectedIndex - 1);
  }

  /**
   * Goes to the next slide.
   * @param {Event} [event] - The event that triggered the navigation.
   */
  next(event) {
    event?.preventDefault();
    if (this.#embla) this.#embla.scrollNext(this.#jump());
    else this.select(this.#selectedIndex + 1);
  }

  /**
   * Navigates slides with the arrow keys while the viewport has focus.
   * @param {KeyboardEvent} event - The keydown event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') this.previous(event);
    else if (event.key === 'ArrowRight') this.next(event);
  }

  /**
   * Whether a programmatic scroll should skip the animation.
   * @param {boolean} [animate=true] - Whether the caller wants an animated scroll.
   */
  #jump(animate = true) {
    return shouldJump(animate);
  }

  async #setupCarousel() {
    const { viewport, slides } = this.refs;
    if (!viewport || !slides || slides.length <= 1) return;

    const EmblaCarousel = await loadEmblaCarousel();

    // The gallery may have been disconnected (e.g. replaced on variant change) while loading.
    if (!EmblaCarousel || !this.isConnected || this.#embla) return;

    this.#embla = createEmblaCarousel(EmblaCarousel, this, viewport, {
      align: 'start',
      containScroll: 'trimSnaps',
      startIndex: this.#selectedIndex,
      // Let 3D models handle their own drag interactions.
      watchDrag: (/** @type {any} */ _, /** @type {Event} */ event) =>
        !(event.target instanceof Element && event.target.closest('model-viewer')),
    });

    this.#embla.on('select', this.#handleSelect).on('reInit', this.#handleSelect);

    this.#handleSelect();
  }

  #handleSelect = () => {
    if (!this.#embla) return;

    const previousIndex = this.#selectedIndex;
    this.#selectedIndex = this.#embla.selectedScrollSnap();
    this.#updateSelection(this.#selectedIndex !== previousIndex);
  };

  /**
   * Syncs the slides, thumbnails and arrows with the selected slide.
   * @param {boolean} [scrollThumbnail=false] - Whether to bring the selected thumbnail into view.
   */
  #updateSelection(scrollThumbnail = false) {
    const { slides = [], thumbnails = [], previous, next } = this.refs;
    const index = this.#selectedIndex;

    slides.forEach((slide, i) => slide.setAttribute('aria-hidden', `${i !== index}`));

    thumbnails.forEach((thumbnail, i) => {
      const selected = i === index;
      thumbnail.setAttribute('aria-selected', `${selected}`);
      SELECTED_THUMBNAIL_CLASSES.forEach((className) => thumbnail.classList.toggle(className, selected));
    });

    const canScrollPrev = this.#embla ? this.#embla.canScrollPrev() : index > 0;
    const canScrollNext = this.#embla ? this.#embla.canScrollNext() : index < slides.length - 1;

    if (previous) previous.disabled = !canScrollPrev;
    if (next) next.disabled = !canScrollNext;

    const selectedThumbnail = thumbnails[index];
    if (scrollThumbnail && selectedThumbnail?.parentElement) {
      scrollIntoView(selectedThumbnail, {
        ancestor: selectedThumbnail.parentElement,
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    if (this.refs.viewport) {
      this.select(event.detail.index, undefined, { animate: false });
    } else {
      this.slideshow?.select(event.detail.index, undefined, { animate: false });
    }
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
